import React, { useEffect, useRef, useState } from 'react';
import { moduleStats, selectedFillInOption } from '../utils/leadersSurvey';
import './LeadersSurveyPage.css';

function initialAnswerState(initialAnswers) {
  const next = {};
  Object.entries(initialAnswers || {}).forEach(([questionId, answer]) => {
    next[questionId] = {
      value: answer?.value ?? (Array.isArray(answer?.value) ? [] : ''),
      fillIn: answer?.fillIn || '',
    };
  });
  return next;
}

function answerFor(answers, question) {
  const current = answers[question.id];
  if (current) return current;
  if (question.type === 'multi') return { value: [], fillIn: '' };
  return { value: '', fillIn: '' };
}

/**
 * One subsection of My Survey. Saves are debounced; the latest value is what gets sent.
 */
const LeadersSurveyView = ({
  title,
  readOnly = false,
  modules,
  initialAnswers,
  progress,
  activeModuleKey,
  activeSectionCode,
  resumeQuestionId,
  onNavigate,
  onSave,
  onMarkComplete,
  autosaveDelay = 600,
}) => {
  const [answers, setAnswers] = useState(() => initialAnswerState(initialAnswers));
  const [saveState, setSaveState] = useState('idle');
  const [markedLocal, setMarkedLocal] = useState({});
  const [completeError, setCompleteError] = useState('');
  const timers = useRef({});
  const latest = useRef({});
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const activeModule = (modules || []).find((mod) => mod.key === activeModuleKey) || modules?.[0];
  const activeSection = activeModule?.subsections?.find((section) => section.code === activeSectionCode)
    || activeModule?.subsections?.[0];

  const flush = async (questionId) => {
    const payload = latest.current[questionId];
    if (!payload || payload.sentGen === payload.gen) return;
    const gen = payload.gen;
    setSaveState('saving');
    try {
      await onSaveRef.current(questionId, { value: payload.value, fillIn: payload.fillIn });
      const current = latest.current[questionId];
      if (!current || current.gen !== gen) {
        await flush(questionId);
        return;
      }
      current.sentGen = gen;
      setSaveState('saved');
    } catch {
      const current = latest.current[questionId];
      if (current && current.gen === gen) setSaveState('error');
    }
  };

  const schedule = (questionId, value, fillIn) => {
    if (readOnly) return;
    const prev = latest.current[questionId] || { gen: 0, sentGen: 0 };
    latest.current[questionId] = {
      value,
      fillIn,
      gen: prev.gen + 1,
      sentGen: prev.sentGen || 0,
    };
    setSaveState('saving');
    clearTimeout(timers.current[questionId]);
    timers.current[questionId] = setTimeout(() => {
      flush(questionId);
    }, autosaveDelay);
  };

  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (!resumeQuestionId) return undefined;
    const node = document.getElementById(`q-${resumeQuestionId}`);
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'center' });
    }
    return undefined;
  }, [resumeQuestionId]);

  if (!activeModule || !activeSection) return null;

  const stats = moduleStats(activeModule, answers);
  const sections = activeModule.subsections;
  const sectionIndex = Math.max(0, sections.findIndex((section) => section.code === activeSection.code));
  const previous = sectionIndex > 0 ? sections[sectionIndex - 1] : null;
  const next = sectionIndex < sections.length - 1 ? sections[sectionIndex + 1] : null;
  const marked = Boolean(progress?.modules?.[activeModule.key]?.markedComplete || markedLocal[activeModule.key]);
  const saveLabel = saveState === 'saving'
    ? 'Saving…'
    : saveState === 'saved'
      ? 'Saved'
      : saveState === 'error'
        ? "Couldn't save"
        : '';

  const commit = (question, value, fillIn) => {
    setAnswers((prev) => ({ ...prev, [question.id]: { value, fillIn } }));
    schedule(question.id, value, fillIn);
  };

  const selectChoice = (question, key) => {
    const option = question.options.find((item) => item.key === key);
    const current = answerFor(answers, question);
    const fillIn = option?.fillIn ? current.fillIn || '' : '';
    commit(question, key, fillIn);
  };

  const toggleMulti = (question, key) => {
    const current = answerFor(answers, question);
    const values = Array.isArray(current.value) ? current.value : [];
    const value = values.includes(key) ? values.filter((item) => item !== key) : [...values, key];
    const fillStillSelected = question.options.some((option) => option.fillIn && value.includes(option.key));
    commit(question, value, fillStillSelected ? current.fillIn || '' : '');
  };

  const updateFill = (question, fillIn) => {
    const current = answerFor(answers, question);
    commit(question, current.value, fillIn);
  };

  const updateText = (question, value) => {
    commit(question, value, '');
  };

  const markComplete = async () => {
    if (readOnly || marked) return;
    setCompleteError('');
    try {
      await onMarkComplete(activeModule.key);
      setMarkedLocal((prev) => ({ ...prev, [activeModule.key]: true }));
    } catch {
      setCompleteError("Couldn't mark this module complete.");
    }
  };

  return (
    <div className="my-survey">
      <p className="my-survey-kicker">{title}</p>
      {readOnly && (
        <p className="my-survey-banner" role="status">
          This survey is closed. You can review your answers, but they can&apos;t be changed.
        </p>
      )}

      {modules.length > 1 && (
        <div className="my-survey-modules" aria-label="Assigned modules">
          {modules.map((mod) => (
            <button
              key={mod.key}
              type="button"
              className="my-survey-chip"
              aria-pressed={mod.key === activeModule.key}
              onClick={() => onNavigate(mod.key, mod.subsections[0].code)}
            >
              {mod.key}. {mod.title}
            </button>
          ))}
        </div>
      )}

      <div className="my-survey-progress-card">
        <div className="my-survey-progress-head">
          <h2 className="my-survey-module-title">{activeModule.key}. {activeModule.title}</h2>
          <p className="my-survey-save" aria-live="polite">{saveLabel}</p>
        </div>
        <div
          className="my-survey-bar"
          role="progressbar"
          aria-valuenow={stats.percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${activeModule.title} progress`}
        >
          <div className="my-survey-bar-fill" style={{ width: `${stats.percent}%` }} />
        </div>
        <p className="my-survey-bar-label">
          {stats.answered} of {stats.total} answered ({stats.percent}%)
          {marked ? ' · Marked complete' : ''}
        </p>
      </div>

      <div className="my-survey-section-head">
        <h3>{activeSection.code}. {activeSection.title}</h3>
        <p>
          Part {sectionIndex + 1} of {sections.length}
        </p>
      </div>

      {activeSection.questions.map((question) => {
        const answer = answerFor(answers, question);
        const fillOption = selectedFillInOption(question, answer);
        return (
          <article key={question.id} id={`q-${question.id}`} className="my-survey-question">
            {(question.type === 'choice' || question.type === 'multi') ? (
              <fieldset disabled={readOnly} className="my-survey-fieldset">
                <legend className="my-survey-qtext">{question.text}</legend>
                {question.help && <p className="my-survey-help">{question.help}</p>}
                {question.options.map((option) => (
                  <label key={option.key} className="my-survey-option">
                    <input
                      type={question.type === 'multi' ? 'checkbox' : 'radio'}
                      name={question.id}
                      value={option.key}
                      checked={question.type === 'multi'
                        ? Array.isArray(answer.value) && answer.value.includes(option.key)
                        : answer.value === option.key}
                      onChange={() => (question.type === 'multi'
                        ? toggleMulti(question, option.key)
                        : selectChoice(question, option.key))}
                    />
                    <span>{option.text}</span>
                  </label>
                ))}
              </fieldset>
            ) : (
              <label className="my-survey-text-label" htmlFor={`input-${question.id}`}>
                <span className="my-survey-qtext">{question.text}</span>
                {question.help && <p className="my-survey-help">{question.help}</p>}
                <textarea
                  id={`input-${question.id}`}
                  rows={question.type === 'comment' ? 4 : 5}
                  value={typeof answer.value === 'string' ? answer.value : ''}
                  onChange={(event) => updateText(question, event.target.value)}
                  disabled={readOnly}
                />
              </label>
            )}
            {fillOption && (
              <label className="my-survey-fill" htmlFor={`fill-${question.id}`}>
                <span>{fillOption.text}</span>
                <input
                  id={`fill-${question.id}`}
                  type="text"
                  value={answer.fillIn || ''}
                  onChange={(event) => updateFill(question, event.target.value)}
                  disabled={readOnly}
                />
              </label>
            )}
          </article>
        );
      })}

      <div className="my-survey-nav">
        <button
          type="button"
          className="my-survey-nav-btn"
          onClick={() => previous && onNavigate(activeModule.key, previous.code)}
          disabled={!previous}
        >
          Previous
        </button>
        <button
          type="button"
          className="my-survey-nav-btn"
          onClick={() => next && onNavigate(activeModule.key, next.code)}
          disabled={!next}
        >
          Next
        </button>
      </div>

      <button
        type="button"
        className="my-survey-complete"
        onClick={markComplete}
        disabled={readOnly || marked}
      >
        {marked ? 'Module marked complete' : 'Mark module complete'}
      </button>
      {completeError && <p className="my-survey-error" role="alert">{completeError}</p>}
      <p className="my-survey-private">Only you can see your answers. They save as you go.</p>
    </div>
  );
};

export default LeadersSurveyView;
